/**
 * Task 14 — Permission override confirmation modal.
 *
 * Mirrors the SSN reveal flow:
 *   1. Super-admin clicks a toggle on the matrix.
 *   2. Modal shows the (role, resource, action) change preview.
 *   3. Reason (>=8) + password fields.
 *   4. Submit → re-verify via /auth/login (stamps fresh iat) →
 *      POST /admin/permissions/override.
 *   5. On 423 permission_locked / 400 reason_too_short / 401
 *      fresh_auth_required, toast the BE detail directly.
 *
 * Reused for both per-cell toggles AND the global "Reset to defaults"
 * by passing `mode = "override" | "reset"`. In "reset" mode the
 * confirmation copy + endpoint change accordingly.
 */
import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
    super_admin: "Super Admin",
    admin: "Admin",
    manager: "Manager",
    customer: "Customer",
};

/**
 * Props:
 *   open, onOpenChange — controlled dialog state
 *   mode = "override" | "reset"
 *   payload — for override: {role, resource, action, allowed:bool (new value), prevAllowed:bool}
 *           — for reset:    {role?: string (null/undefined = all), overrideCount: number}
 *   onSuccess — callback after a successful POST (matrix should refresh)
 */
export default function PermissionOverrideModal({
    open, onOpenChange, mode = "override", payload = {}, onSuccess,
}) {
    const { user } = useAuth();
    const [password, setPassword] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            setPassword("");
            setReason("");
            setBusy(false);
        }
    }, [open]);

    const reasonOk = reason.trim().length >= 8;
    const canSubmit = password && reasonOk && !busy;

    async function submit() {
        if (!canSubmit) return;
        setBusy(true);
        try {
            // Re-verify password → fresh iat token (mirrors SSN modal).
            try {
                const { data: login } = await api.post("/auth/login", {
                    email: user?.email, password,
                });
                localStorage.setItem("rp_token", login.token);
            } catch {
                toast.error("Password incorrect");
                setBusy(false);
                return;
            }
            // Now hit the appropriate endpoint.
            if (mode === "override") {
                await api.post("/admin/permissions/override", {
                    role: payload.role,
                    resource: payload.resource,
                    action: payload.action,
                    allowed: !!payload.allowed,
                    reason: reason.trim(),
                });
                toast.success(
                    `${ROLE_LABEL[payload.role] ?? payload.role}: ${payload.allowed ? "granted" : "denied"} ${payload.resource} / ${payload.action}`,
                );
            } else {
                const body = { reason: reason.trim() };
                if (payload.role) body.role = payload.role;
                const { data } = await api.post("/admin/permissions/reset", body);
                toast.success(`Reset complete — ${data?.removed ?? 0} override(s) removed`);
            }
            onSuccess?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    const isReset = mode === "reset";
    const Icon = isReset ? RotateCcw : Shield;
    const title = isReset
        ? (payload.role ? `Reset ${ROLE_LABEL[payload.role] ?? payload.role} overrides`
                         : "Reset all permission overrides")
        : "Confirm permission change";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="permission-override-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Icon size={16} className={isReset ? "text-amber-600" : "text-blue-700"} />
                        {title}
                    </DialogTitle>
                    <DialogDescription>
                        {isReset ? (
                            <>
                                This removes {payload.overrideCount ?? 0} override(s)
                                {payload.role ? <> for the <span className="font-medium">{ROLE_LABEL[payload.role] ?? payload.role}</span> role</> : null}
                                . Affected roles revert to catalog defaults.
                            </>
                        ) : (
                            <>
                                <span className="font-medium">{payload.allowed ? "Grant" : "Revoke"}</span>{" "}
                                <code className="px-1 py-0.5 rounded bg-slate-100 text-xs">{payload.resource}</code> /{" "}
                                <code className="px-1 py-0.5 rounded bg-slate-100 text-xs">{payload.action}</code>{" "}
                                for the <span className="font-medium">{ROLE_LABEL[payload.role] ?? payload.role}</span> role.
                                This change is audit-logged.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="perm-reason">Reason (min 8 characters)</Label>
                        <Textarea
                            id="perm-reason"
                            data-testid="perm-modal-reason"
                            placeholder="e.g. Ticket TKT-1234 — on-call manager needs to triage urgent customer issue"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            className={reason && !reasonOk ? "border-red-500" : ""}
                        />
                        {reason && !reasonOk && (
                            <div className="text-xs text-red-600">Reason must be at least 8 characters.</div>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="perm-pwd">Your password</Label>
                        <Input
                            id="perm-pwd"
                            data-testid="perm-modal-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="perm-modal-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="perm-modal-confirm"
                        className={isReset ? "bg-amber-600 hover:bg-amber-700" : ""}
                    >
                        {busy && <Loader2 size={14} className="mr-2 animate-spin" />}
                        {isReset ? "Reset" : "Confirm"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
